import {
  INBOX_SURFACES,
  type InboxSurface,
  STRATEGY_KEYS,
  strategyKeysFor,
  strategyTakesScope,
} from '@sbc/domain-models/gateway';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAgents } from '@/lib/api/agents';
import { useChannelConnections } from '@/lib/api/channel-connections';
import {
  resolveStrategyKey,
  SELECTABLE_STRATEGY_KEYS,
  STRATEGIES,
} from '@/routes/gateway/strategies';

export interface BindingFormValues {
  channelConnectionId: string;
  strategyKey: (typeof STRATEGY_KEYS)[number];
  agentId: string;
  enabled: boolean;
  surface: InboxSurface | null;
  externalScope: string | null;
}

const ANY_SURFACE = 'any';

const SURFACE_LABELS: Record<InboxSurface, string> = {
  comment_thread: 'Public comments only',
  dm: 'Direct messages only',
};

interface BindingFormProps {
  initial?: BindingFormValues;
  submitting: boolean;
  submitLabel: string;
  error?: string | null;
  onSubmit: (values: BindingFormValues) => void;
  onCancel: () => void;
}

export function BindingForm({
  initial,
  submitting,
  submitLabel,
  error,
  onSubmit,
  onCancel,
}: BindingFormProps) {
  const connectionsQuery = useChannelConnections({ page: 1, pageSize: 100 });
  const agentsQuery = useAgents({ page: 1, pageSize: 100 });

  const [channelConnectionId, setConnectionId] = useState(initial?.channelConnectionId ?? '');
  const [strategyKey, setStrategyKey] = useState<(typeof STRATEGY_KEYS)[number]>(
    initial?.strategyKey ?? STRATEGY_KEYS[0],
  );
  const [agentId, setAgentId] = useState(initial?.agentId ?? '');
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [surface, setSurface] = useState<InboxSurface | null>(initial?.surface ?? null);
  const [externalScope, setExternalScope] = useState(initial?.externalScope ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  // A strategy belongs to exactly one channel, so the picker offers only what the selected
  // connection can actually run — an Instagram connection must never be bound to the Intercom
  // relay strategy. Before a connection is picked there is nothing to scope by, so offer all.
  const connections = connectionsQuery.data?.items ?? [];
  const selectedChannel = connections.find((c) => c.id === channelConnectionId)?.channel;
  const strategyKeys = selectedChannel
    ? strategyKeysFor(selectedChannel)
    : SELECTABLE_STRATEGY_KEYS;

  const effectiveStrategyKey = resolveStrategyKey(selectedChannel, strategyKey);
  const takesChannelScope = strategyTakesScope(effectiveStrategyKey);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!channelConnectionId) {
      setLocalError('Pick a connection.');
      return;
    }
    if (!agentId) {
      setLocalError('Pick an agent.');
      return;
    }
    setLocalError(null);
    onSubmit({
      channelConnectionId,
      strategyKey: effectiveStrategyKey,
      agentId,
      enabled,
      surface: effectiveStrategyKey === 'draft-on-inbound' ? surface : null,
      externalScope: takesChannelScope ? externalScope.trim() || null : null,
    });
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <Label htmlFor="connection">Connection</Label>
        <Select required value={channelConnectionId} onValueChange={setConnectionId}>
          <SelectTrigger id="connection">
            <SelectValue placeholder="Select a connection…" />
          </SelectTrigger>
          <SelectContent>
            {connections.map((connection) => (
              <SelectItem key={connection.id} value={connection.id}>
                {connection.handle} ({connection.channel})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="strategy">Strategy</Label>
        <Select
          value={effectiveStrategyKey}
          onValueChange={(value) =>
            setStrategyKey(strategyKeys.find((key) => key === value) ?? effectiveStrategyKey)
          }
        >
          <SelectTrigger id="strategy">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {strategyKeys.map((key) => (
              <SelectItem key={key} value={key}>
                {STRATEGIES[key].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          {STRATEGIES[effectiveStrategyKey].help}
        </p>
      </div>

      {effectiveStrategyKey === 'draft-on-inbound' && (
        <div className="space-y-2">
          <Label htmlFor="surface">Surface</Label>
          <Select
            value={surface ?? ANY_SURFACE}
            onValueChange={(value) =>
              setSurface(INBOX_SURFACES.find((option) => option === value) ?? null)
            }
          >
            <SelectTrigger id="surface">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_SURFACE}>Any surface</SelectItem>
              {INBOX_SURFACES.map((option) => (
                <SelectItem key={option} value={option}>
                  {SURFACE_LABELS[option]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            A surface-specific binding wins over an "any surface" one, so a connection can run a
            KB-only agent on public comments and an account-aware agent on DMs.
          </p>
        </div>
      )}

      {takesChannelScope && (
        <div className="space-y-2">
          <Label htmlFor="externalScope">Slack channel id</Label>
          <Input
            id="externalScope"
            required
            maxLength={120}
            placeholder="C0123ABCDEF"
            value={externalScope}
            onChange={(event) => setExternalScope(event.target.value)}
          />
          <p className="text-xs text-[var(--color-muted-foreground)]">
            The one channel this binding answers every message in (Channel details → bottom). The
            bot also has to be invited to it.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="agent">Agent</Label>
        <Select required value={agentId} onValueChange={setAgentId}>
          <SelectTrigger id="agent">
            <SelectValue placeholder="Select an agent…" />
          </SelectTrigger>
          <SelectContent>
            {agentsQuery.data?.items.map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>
                {agent.activeVersion.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Runs the agent's active version, so saving a new version puts it live here.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        Enabled
      </label>

      {(localError ?? error) && (
        <p className="text-sm text-[var(--color-destructive)]">{localError ?? error}</p>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
